import * as React from 'react';
import { Link as RouterLink } from 'react-router';
import { AppBar, Avatar, Badge, Button, IconButton, Stack, Toolbar, Typography } from '@mui/material';
import { ExpandMore, Menu, MenuOpen, Notifications } from '@mui/icons-material';

import { AuthContext } from '@modules/Auth/contexts';
import { Authenticated } from '@modules/Auth/components';
import { UserContext } from '@modules/Users/contexts';
import { CompanyContext } from '@modules/Companies/contexts';
import { CompanyAutocomplete } from '@modules/Companies/components/CompanyAutocomplete';
import { getUserInitials } from '@modules/Users/helpers/user';

import { useRouter } from '@cvt/routing';

import config from '@shared/config';

import { UserMenu } from './UserMenu';
import { HeaderNav } from './HeaderNav';


type Props = {
  onNavigationClickRequest?: () => void;
  navigationOpen?: boolean;
};

export const Header: React.FC<React.PropsWithChildren<Props>> = ({ onNavigationClickRequest, navigationOpen, children }) => {

  const router = useRouter();
  const { user } = React.useContext(AuthContext);
  const { isAdmin } = React.useContext(UserContext);
  const { company = null, selectCompany } = React.useContext(CompanyContext);

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        position: 'fixed',
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
      }}
      elevation={0}
    >
      <Toolbar
        sx={{
          backgroundColor: 'background.paper',
          color: 'primary.main',
        }}
      >
        {onNavigationClickRequest && (
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label={navigationOpen ? 'Close navigation' : 'Open navigation'}
            sx={{ mr: 2 }}
            onClick={onNavigationClickRequest}
          >
            {navigationOpen ? <MenuOpen/> : <Menu/>}
          </IconButton>
        )}

        <Stack direction="row" flexGrow={1} alignItems="center" justifyContent="flex-start">
          <Typography  component={RouterLink} to={router.home.path} color="text.primary" fontSize="1.25rem" fontWeight={600} sx={{ textDecoration: 'none' }}>
            {config.projectName}
          </Typography>

          <Stack flexGrow={1}>
            {children}
          </Stack>

          <Stack
            direction="row"
            flexGrow={1}
            alignItems="center"
            justifyContent="center"
          >
            <HeaderNav/>
          </Stack>
        </Stack>

        <Stack flexGrow={0} direction="row" alignItems="center" spacing={2}>
          {isAdmin && (
            <CompanyAutocomplete
              sx={{ minWidth: 250 }}
              size="small"
              label="Customer"
              value={company}
              onChange={(e, value) => selectCompany(value)}
              TextFieldProps={{
                color: 'secondary',
              }}
            />
          )}
          {config.featureFlags.notifications && (
            <Authenticated>
              <IconButton
                size="large"
                color="inherit"
              >
                <Badge badgeContent={0} color="error">
                  <Notifications/>
                </Badge>
              </IconButton>
            </Authenticated>
          )}
          <UserMenu anchorEl={anchorElUser} onCloseRequest={handleCloseUserMenu}>
            <Button onClick={handleOpenUserMenu} sx={{ gap: 1 }}>
              <Avatar
                alt={user?.displayName}
                src={user?.profilePicture || undefined}
                sx={{ width: 32, height: 32, fontSize: 14, fontWeight: '600', bgcolor: 'primary.main', color: 'background.paper' }}
              >
                {getUserInitials(user?.displayName || '')}
              </Avatar>
              {user?.displayName && (
                <>
                  <Typography variant="h6" color="textPrimary" lineHeight="unset" fontWeight={600}>{user?.displayName}</Typography>
                  <ExpandMore
                    fontSize="small"
                    sx={theme => ({ color: theme.palette.text.primary })}
                  />
                </>
              )}
            </Button>
          </UserMenu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
